import React, { useEffect, useMemo, useReducer } from "react";
import U2000InputCalendarCustom from "../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from "../../../u2000Core/reducer/formReducer/U2000FormState";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";

const WIZARD_INVOICE_DATE_FORMAT = "yyyy-MM-dd";

type DateRow = { invoiceDate: string };

const dateFormInitial = (invoiceDate: string): U2000FormState<DateRow> => ({
  ...u2000IntFormState,
  current: { invoiceDate },
  old: { invoiceDate },
  state: U2000FormStateEnumState.EDIT,
  editMode: U2000FormStateEnumEditMode.EDIT,
  touched: {},
  errors: {},
  otherErrors: {},
  isSubmitted: false,
  customAttributes: {},
});

export type U3125ForfaitWizardInvoiceDateInputProps = {
  value: string;
  onChange: (invoiceDate: string) => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  inlineEnd?: React.ReactNode;
};

/** Saisie date facture wizard — wrapper U2000InputCalendarCustom (format yyyy-MM-dd). */
const U3125ForfaitWizardInvoiceDateInput: React.FC<U3125ForfaitWizardInvoiceDateInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  required = true,
  inlineEnd,
}) => {
  const [formState, formStateDispatch] = useReducer(u2000FormState, undefined, () => dateFormInitial(value));
  const baseHandlers = useU2000FormStateHandlers<DateRow>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => ({ invoiceDate: "" }),
    },
  });

  useEffect(() => {
    const current = baseHandlers.getData()?.invoiceDate ?? "";
    if (current !== value) {
      baseHandlers.setData({ invoiceDate: value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const formStateHandlers = useMemo(
    () => ({
      ...baseHandlers,
      setData: (row: DateRow) => {
        baseHandlers.setData(row);
        onChange(row.invoiceDate ?? "");
      },
    }),
    [baseHandlers, onChange],
  );

  return (
    <div className="w-fit max-w-full min-w-44">
      <U2000InputCalendarCustom
        formStateHandlers={formStateHandlers}
        fieldName="invoiceDate"
        label={label}
        readonly={disabled}
        required={required}
        dateFormat={WIZARD_INVOICE_DATE_FORMAT}
        placeholder={WIZARD_INVOICE_DATE_FORMAT}
        inlineEnd={inlineEnd}
      />
    </div>
  );
};

export default U3125ForfaitWizardInvoiceDateInput;
